import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, Phone, Mail, Calendar, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { CustomerCommunicationTab } from "@/components/admin/CustomerCommunicationTab";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { formatDistanceToNow } from "date-fns";
import type { Customer, Activity } from "@/types/crm";
import { LEAD_SCORES, LEAD_SOURCES, getLeadScoreConfig, formatCurrency } from "@/types/crm";
import type { LeadScore, LeadSource } from "@/types/crm";
import { logAudit, computeChanges } from "@/utils/auditLog";
import { useAuth } from "@/hooks/useAuth";
import { validatePhone, validateCanadianPostalCode } from "@/utils/validation";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";

const CANADIAN_PROVINCES = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" }
];

export default function CustomerProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState<Partial<Customer>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  
  // Related Data
  const [deals, setDeals] = useState<any[]>([]);
  const [interestedVehicles, setInterestedVehicles] = useState<any[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [inventory, setInventory] = useState<any[]>([]);
  const [profiles, setProfiles] = useState<Record<string, string>>({});
  const [allCustomers, setAllCustomers] = useState<{id: string, first_name: string, last_name: string}[]>([]);
  
  // UI States
  const [selectedVehicleToAttach, setSelectedVehicleToAttach] = useState("none");
  const [isAttaching, setIsAttaching] = useState(false);
  const [newNote, setNewNote] = useState("");

  useEffect(() => {
    if (id) {
      loadCustomerData(id);
      loadStaticData();
    }
  }, [id]);

  // Dirty-checking: compare all editable fields
  const isDirty = customer ? (
    formData.first_name !== customer.first_name ||
    formData.last_name !== customer.last_name ||
    (formData.email || '') !== (customer.email || '') ||
    (formData.phone || '') !== (customer.phone || '') ||
    (formData.address || '') !== (customer.address || '') ||
    (formData.city || '') !== (customer.city || '') ||
    (formData.province || '') !== (customer.province || '') ||
    (formData.postal_code || '') !== (customer.postal_code || '') ||
    (formData.date_of_birth || '') !== (customer.date_of_birth || '') ||
    (formData.credit_rating || '') !== (customer.credit_rating || '') ||
    (formData.lead_score) !== customer.lead_score ||
    (formData.lead_source) !== customer.lead_source ||
    (formData.assigned_to || null) !== (customer.assigned_to || null) ||
    (formData.next_follow_up_at || null) !== (customer.next_follow_up_at || null) ||
    (formData.referred_by || null) !== (customer.referred_by || null) ||
    (formData.sms_opted_in) !== customer.sms_opted_in ||
    (formData.email_opted_in) !== customer.email_opted_in ||
    (formData.driver_license || '') !== (customer.driver_license || '') ||
    (formData.driver_license_expiry || '') !== (customer.driver_license_expiry || '')
  ) : false;

  // Browser tab close guard
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const handleBack = useCallback(() => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    navigate('/admin/customers');
  }, [isDirty, navigate]);

  const loadStaticData = async () => {
    const { data: invData } = await supabase.from("vehicles").select("id, year, make, model, status").eq("status", "available");
    if (invData) setInventory(invData);

    const { data: profData } = await supabase.from("profiles").select("id, full_name");
    if (profData) {
      const map: Record<string, string> = {};
      profData.forEach(p => { map[p.id] = p.full_name; });
      setProfiles(map);
    }

    const { data: custData } = await supabase.from("customers").select("id, first_name, last_name").is("deleted_at", null).order("last_name");
    if (custData) setAllCustomers(custData);
  };

  const loadCustomerData = async (customerId: string) => {
    setIsLoading(true);
    
    // Customer
    const { data: custData, error: custErr } = await supabase.from("customers").select("*").eq("id", customerId).is("deleted_at", null).single();
    if (custErr || !custData) {
      toast.error("Customer not found");
      navigate("/admin/customers");
      return;
    }
    setCustomer(custData as Customer);
    setFormData(custData as Customer);

    // Deals
    const { data: dealsData } = await supabase.from("deals").select(`
      *, vehicle:vehicles(id, year, make, model, vin, price, vehicle_images(image_url, is_primary))
    `).eq("customer_id", customerId).order("created_at", { ascending: false });
    setDeals(dealsData || []);

    // Interested Vehicles
    const { data: intVehData } = await supabase.from("customer_interested_vehicles").select(`
      id, created_at, vehicle:vehicles(id, year, make, model, price, vin, vehicle_images(image_url, is_primary))
    `).eq("customer_id", customerId).order("created_at", { ascending: false });
    setInterestedVehicles(intVehData || []);

    // Activities
    const { data: actData } = await supabase.from("activities").select("*").eq("customer_id", customerId).order("created_at", { ascending: false }).limit(50);
    setActivities((actData as Activity[]) || []);

    setIsLoading(false);
  };

  const handleSave = async () => {
    if (!customer) return;

    // Basic email validation
    const email = formData.email?.trim() || null;
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    const phoneVal = formData.phone?.trim() || "";
    if (phoneVal && !validatePhone(phoneVal)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    const postalVal = formData.postal_code?.trim() || "";
    if (postalVal && !validateCanadianPostalCode(postalVal)) {
      toast.error("Please enter a valid Canadian postal code (e.g., M5V 1A1)");
      return;
    }

    setIsSaving(true);
    try {
      // Email uniqueness check (MED-4)
      if (email && email !== customer.email) {
        const { data: existingEmail } = await supabase
          .from("customers")
          .select("id")
          .eq("email", email)
          .is("deleted_at", null)
          .neq("id", customer.id)
          .maybeSingle();

        if (existingEmail) {
          toast.error("A customer with this email address already exists.");
          setIsSaving(false);
          return;
        }
      }
      const payload = {
        first_name: formData.first_name?.trim() || customer.first_name,
        last_name: formData.last_name?.trim() || customer.last_name,
        email,
        phone: formData.phone?.replace(/\D/g, '') || null,
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        province: formData.province?.trim() || null,
        postal_code: formData.postal_code?.trim() ? formData.postal_code.trim().toUpperCase() : null,
        date_of_birth: formData.date_of_birth || null,
        credit_rating: formData.credit_rating || null,
        lead_score: formData.lead_score || customer.lead_score,
        lead_source: formData.lead_source || customer.lead_source,
        assigned_to: formData.assigned_to || null,
        next_follow_up_at: formData.next_follow_up_at || null,
        sms_opted_in: formData.sms_opted_in ?? customer.sms_opted_in,
        email_opted_in: formData.email_opted_in ?? customer.email_opted_in,
        referred_by: formData.referred_by || null,
        driver_license: formData.driver_license?.trim() || null,
        driver_license_expiry: formData.driver_license_expiry || null,
      };

      const { error } = await supabase.from("customers").update(payload).eq("id", customer.id);
      if (error) throw error;
      
      const updated = { ...customer, ...payload };
      setCustomer(updated as Customer);
      setFormData(updated as Customer);
      toast.success("Customer profile updated successfully");
      // Compute changes for audit
      const changes = computeChanges(
        customer as unknown as Record<string, unknown>,
        payload as unknown as Record<string, unknown>,
        ['first_name', 'last_name', 'email', 'phone', 'address', 'city', 'province', 'postal_code', 'date_of_birth', 'credit_rating', 'lead_score', 'lead_source', 'assigned_to', 'next_follow_up_at', 'sms_opted_in', 'email_opted_in', 'referred_by', 'driver_license', 'driver_license_expiry']
      );
      await logAudit({
        entityType: 'customer',
        entityId: customer.id,
        action: 'updated',
        changes: Object.keys(changes).length > 0 ? changes : undefined,
        metadata: { customer_name: `${payload.first_name} ${payload.last_name}` },
        performedBy: user?.id,
      });
    } catch (err) {
      toast.error("Failed to update profile");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  useKeyboardShortcut(["ctrl", "s"], handleSave);

  const handleDelete = async () => {
    if (!customer) return;
    
    // Block deleting customer if they have funded or delivered deals
    const hasActiveDeals = deals.some(d => d.stage === "funded" || d.stage === "delivered");
    if (hasActiveDeals) {
      toast.error("Cannot delete a customer with funded or delivered deals");
      setIsDeleteConfirmOpen(false);
      return;
    }

    // Soft delete customer
    const { error } = await supabase
      .from('customers')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', customer.id);

    if (error) {
      toast.error("Failed to delete customer");
      console.error(error);
    } else {
      toast.success("Customer deleted successfully");
      await logAudit({
        entityType: 'customer',
        entityId: customer.id,
        action: 'deleted',
        metadata: { customer_name: `${customer.first_name} ${customer.last_name}` },
        performedBy: user?.id,
      });
      navigate("/admin/customers");
    }
  };

  const attachVehicle = async () => {
    if (!customer || selectedVehicleToAttach === "none") return;
    setIsAttaching(true);
    const { error } = await supabase.from("customer_interested_vehicles").insert({
      customer_id: customer.id,
      vehicle_id: selectedVehicleToAttach
    });

    if (error) {
      if (error.code === '23505') toast.error("Vehicle is already attached to this customer");
      else toast.error("Failed to attach vehicle");
    } else {
      toast.success("Vehicle attached successfully");
      setSelectedVehicleToAttach("none");
      // Targeted reload: only interested vehicles
      const { data: iv } = await supabase.from("customer_interested_vehicles").select(`id, created_at, vehicle:vehicles(id, year, make, model, price, vin, vehicle_images(image_url, is_primary))`).eq("customer_id", customer.id).order("created_at", { ascending: false });
      setInterestedVehicles(iv || []);
    }
    setIsAttaching(false);
  };

  const detachVehicle = async (interestId: string) => {
    const { error } = await supabase.from("customer_interested_vehicles").delete().eq("id", interestId);
    if (error) toast.error("Failed to detach vehicle");
    else {
      toast.success("Vehicle detached");
      if (customer) {
        const { data: iv } = await supabase.from("customer_interested_vehicles").select(`id, created_at, vehicle:vehicles(id, year, make, model, price, vin, vehicle_images(image_url, is_primary))`).eq("customer_id", customer.id).order("created_at", { ascending: false });
        setInterestedVehicles(iv || []);
      }
    }
  };

  const addNote = async () => {
    if (!customer || !newNote.trim()) return;
    const { error } = await supabase.from("activities").insert({
      customer_id: customer.id, type: "note" as const, subject: "Note added", description: newNote.trim(),
    });

    if (error) toast.error("Failed to add note");
    else {
      setNewNote("");
      toast.success("Note added");
      // Targeted reload: only activities
      const { data: actData } = await supabase.from("activities").select("*").eq("customer_id", customer.id).order("created_at", { ascending: false }).limit(50);
      setActivities((actData as Activity[]) || []);
    }
  };

  if (isLoading || !customer) return (
    <div className="space-y-6 animate-pulse">
      <div className="bg-card p-6 rounded-lg border shadow-sm flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-muted" />
        <div className="space-y-2 flex-1"><div className="h-6 bg-muted rounded w-48" /><div className="h-4 bg-muted rounded w-64" /></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="bg-card border rounded-lg p-5"><div className="h-3 bg-muted rounded w-20 mb-2" /><div className="h-7 bg-muted rounded w-24" /></div>)}</div>
      <div className="bg-card border rounded-lg p-6 space-y-4"><div className="h-4 bg-muted rounded w-32" /><div className="h-10 bg-muted rounded" /><div className="h-10 bg-muted rounded" /></div>
    </div>
  );

  // Live header values that reflect form edits in real time
  const liveSc = getLeadScoreConfig(formData.lead_score || customer.lead_score);
  const liveSource = LEAD_SOURCES.find(s => s.key === (formData.lead_source || customer.lead_source));
  
  // Calculate Stats
  let totalSpend = 0;
  deals.forEach(d => {
    if (d.stage === 'funded' || d.stage === 'delivered') totalSpend += (Number(d.sale_price) || 0);
  });

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-card p-6 rounded-lg border shadow-sm">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack}><ArrowLeft className="h-5 w-5" /></Button>
          <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-2xl font-bold flex-shrink-0">
            {(formData.first_name || customer.first_name).charAt(0)}{(formData.last_name || customer.last_name).charAt(0)}
          </div>
          <div>
            <h1 className="text-3xl font-heading font-bold">{formData.first_name || customer.first_name} {formData.last_name || customer.last_name}</h1>
            <div className="flex items-center gap-3 text-muted-foreground mt-1 text-sm">
              <span className="flex items-center"><Phone className="h-3 w-3 mr-1" /> {formData.phone || customer.phone || 'No phone'}</span>
              <span className="flex items-center"><Mail className="h-3 w-3 mr-1" /> {formData.email || customer.email || 'No email'}</span>
            </div>
            <div className="flex gap-2 mt-2">
              <Badge style={{ backgroundColor: liveSc.bg, color: liveSc.color, borderColor: liveSc.border }} className="border">{liveSc.label} Lead</Badge>
              <Badge variant="outline" className="bg-muted">{liveSource?.label || formData.lead_source || customer.lead_source}</Badge>
            </div>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setIsDeleteConfirmOpen(true)}>
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSave} disabled={isSaving}>
            <Save className="w-4 h-4 mr-2" /> {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-5 pb-4">
          <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Lifetime Spend</div>
          <div className="text-2xl font-bold text-green-600">{formatCurrency(totalSpend)}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Active Deals</div>
          <div className="text-2xl font-bold">{deals.filter(d => d.stage !== 'funded' && d.stage !== 'lost').length}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Since</div>
          <div className="text-2xl font-bold">{new Date(customer.created_at).toLocaleDateString('en-CA', { month: 'short', year: 'numeric' })}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Last Contact</div>
          <div className="text-2xl font-bold text-blue-600">
            {customer.last_contacted_at ? formatDistanceToNow(new Date(customer.last_contacted_at), { addSuffix: true }) : 'Never'}
          </div>
        </CardContent></Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="details" className="w-full">
        <TabsList className="w-full justify-start h-auto p-1 bg-card border rounded-lg overflow-x-auto">
          <TabsTrigger value="details" className="text-sm font-semibold py-2 px-6">Profile Details</TabsTrigger>
          <TabsTrigger value="vehicles" className="text-sm font-semibold py-2 px-6">Vehicles & Deals</TabsTrigger>
          <TabsTrigger value="comms" className="text-sm font-semibold py-2 px-6">Communications</TabsTrigger>
          <TabsTrigger value="notes" className="text-sm font-semibold py-2 px-6">Internal Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Core Info */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Contact Information</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>First Name</Label><Input maxLength={100} value={formData.first_name || ""} onChange={e => setFormData({ ...formData, first_name: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Last Name</Label><Input maxLength={100} value={formData.last_name || ""} onChange={e => setFormData({ ...formData, last_name: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Email Address</Label><Input type="email" maxLength={255} value={formData.email || ""} onChange={e => setFormData({ ...formData, email: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Phone Number</Label><Input type="tel" maxLength={20} value={formData.phone || ""} onChange={e => setFormData({ ...formData, phone: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Street Address</Label><Input maxLength={255} value={formData.address || ""} onChange={e => setFormData({ ...formData, address: e.target.value })} /></div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>City</Label><Input maxLength={100} value={formData.city || ""} onChange={e => setFormData({ ...formData, city: e.target.value })} /></div>
                  <div className="space-y-2">
                    <Label>Province</Label>
                    <Select value={formData.province || "ON"} onValueChange={v => setFormData({ ...formData, province: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CANADIAN_PROVINCES.map(p => (
                          <SelectItem key={p.code} value={p.code}>{p.code}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Postal Code</Label><Input maxLength={10} value={formData.postal_code || ""} onChange={e => setFormData({ ...formData, postal_code: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Date of Birth</Label><Input type="date" value={formData.date_of_birth || ""} onChange={e => setFormData({ ...formData, date_of_birth: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Credit Rating</Label>
                    <Select value={formData.credit_rating || "unknown"} onValueChange={v => setFormData({ ...formData, credit_rating: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="excellent">Excellent (750+)</SelectItem><SelectItem value="good">Good (700-749)</SelectItem><SelectItem value="fair">Fair (650-699)</SelectItem><SelectItem value="poor">Poor (&lt;650)</SelectItem><SelectItem value="unknown">Unknown</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Driver's License No.</Label><Input maxLength={50} value={formData.driver_license || ""} onChange={e => setFormData({ ...formData, driver_license: e.target.value })} placeholder="e.g. R4320-40027-50304" /></div>
                  <div className="space-y-2"><Label>Driver's License Expiry</Label><Input type="date" value={formData.driver_license_expiry || ""} onChange={e => setFormData({ ...formData, driver_license_expiry: e.target.value })} /></div>
                </div>
              </CardContent>
            </Card>

            {/* CRM Properties */}
            <Card>
              <CardHeader><CardTitle className="text-lg">CRM Properties</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Lead Score</Label>
                    <Select value={formData.lead_score || "cold"} onValueChange={v => setFormData({ ...formData, lead_score: v as LeadScore })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{LEAD_SCORES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Lead Source</Label>
                    <Select value={formData.lead_source || "other"} onValueChange={v => setFormData({ ...formData, lead_source: v as LeadSource })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{LEAD_SOURCES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Assigned Representative</Label>
                    <Select value={formData.assigned_to || "unassigned"} onValueChange={v => setFormData({ ...formData, assigned_to: v === "unassigned" ? null : v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {Object.entries(profiles).map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Next Follow-Up</Label><Input type="datetime-local" value={formData.next_follow_up_at ? new Date(formData.next_follow_up_at).toISOString().slice(0, 16) : ""} onChange={e => setFormData({ ...formData, next_follow_up_at: e.target.value ? new Date(e.target.value).toISOString() : null })} /></div>
                  <div className="space-y-2"><Label>Referred By</Label>
                    <Select value={formData.referred_by || "none"} onValueChange={v => setFormData({ ...formData, referred_by: v === "none" ? null : v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        <SelectItem value="none">No referral</SelectItem>
                        {allCustomers.filter(c => c.id !== customer?.id).map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="pt-4 border-t space-y-4">
                  <Label className="text-base font-semibold">Marketing Preferences</Label>
                  <div className="flex items-center justify-between">
                    <div><Label className="font-semibold">SMS Opt-In</Label><p className="text-sm text-muted-foreground">Consent to receive text messages.</p></div>
                    <Switch checked={formData.sms_opted_in || false} onCheckedChange={v => setFormData({ ...formData, sms_opted_in: v })} />
                  </div>
                  <div className="flex items-center justify-between">
                    <div><Label className="font-semibold">Email Opt-In</Label><p className="text-sm text-muted-foreground">Consent to receive promotional emails.</p></div>
                    <Switch checked={formData.email_opted_in || false} onCheckedChange={v => setFormData({ ...formData, email_opted_in: v })} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="vehicles" className="mt-6 space-y-6">
          {/* Interested Vehicles */}
          <Card>
            <CardHeader><CardTitle className="text-lg flex justify-between items-center">Attached Vehicles
              <div className="flex gap-2 items-center w-72">
                <Select value={selectedVehicleToAttach} onValueChange={setSelectedVehicleToAttach}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Select vehicle..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select vehicle...</SelectItem>
                    {inventory.map(v => <SelectItem key={v.id} value={v.id}>{v.year} {v.make} {v.model}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={attachVehicle} disabled={isAttaching || selectedVehicleToAttach === "none"}>Attach</Button>
              </div>
            </CardTitle></CardHeader>
            <CardContent>
              {interestedVehicles.length === 0 ? <p className="text-muted-foreground text-center py-6 border border-dashed rounded-lg">No vehicles attached yet.</p> : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {interestedVehicles.map(item => (
                    <div key={item.id} className="border rounded-lg overflow-hidden flex bg-card shadow-sm">
                       <div className="w-24 h-24 bg-muted border-r flex-shrink-0">
                         {item.vehicle?.vehicle_images?.[0]?.image_url ? 
                           <img src={item.vehicle.vehicle_images[0].image_url} alt="Vehicle" className="w-full h-full object-cover" /> :
                           <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">No Image</div>
                         }
                       </div>
                       <div className="p-3 flex-1 flex flex-col justify-center relative">
                         <Button variant="ghost" size="icon" className="absolute top-1 right-1 text-red-500 h-6 w-6" onClick={() => detachVehicle(item.id)}><Trash2 className="h-3 w-3" /></Button>
                         <h4 className="font-bold text-sm">{item.vehicle?.year} {item.vehicle?.make} {item.vehicle?.model}</h4>
                         <div className="text-xs text-muted-foreground mt-1">VIN: {item.vehicle?.vin}</div>
                         <div className="font-bold text-primary mt-1">{formatCurrency(item.vehicle?.price)}</div>
                       </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Past Deals */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Deal History</CardTitle></CardHeader>
            <CardContent>
              {deals.length === 0 ? <p className="text-muted-foreground text-center py-6 border border-dashed rounded-lg">No deals recorded.</p> : (
                <div className="space-y-3">
                  {deals.map(deal => (
                    <div key={deal.id} className="border rounded-lg p-4 flex flex-col md:flex-row justify-between items-start md:items-center bg-card shadow-sm gap-4">
                      <div>
                        <h4 className="font-bold text-base">{deal.title}</h4>
                        <div className="text-sm text-muted-foreground">Deal #{deal.deal_number} • Stage: <Badge variant="secondary" className="ml-1">{deal.stage}</Badge></div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">Sale Price</div>
                          <div className="font-bold text-lg">{formatCurrency(deal.sale_price)}</div>
                        </div>
                        <Button variant="outline" onClick={() => navigate("/admin/deals")}>View Deal</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="comms" className="mt-6">
          <Card>
            <CardContent className="p-4">
              <CustomerCommunicationTab customerId={customer.id} customerName={`${customer.first_name} ${customer.last_name}`} customerPhone={customer.phone} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes" className="mt-6 space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-lg">Add Internal Note</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Textarea maxLength={2000} value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Type a note here... (only visible to team members)" rows={3} />
              <Button onClick={addNote} disabled={!newNote.trim()}>Save Note</Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-lg">Activity History</CardTitle></CardHeader>
            <CardContent>
              {activities.length === 0 ? <p className="text-muted-foreground text-center py-6">No activity recorded yet.</p> : (
                <div className="space-y-3 relative ml-5 border-l-2 border-slate-200 pl-6">
                  {activities.map((act) => (
                    <div key={act.id} className="relative">
                      <div className="absolute -left-[31px] top-1 flex items-center justify-center w-8 h-8 rounded-full border-2 border-white bg-slate-100 text-slate-500 shadow-sm">
                        {act.type === 'note' ? <FileText className="w-3.5 h-3.5" /> : <Calendar className="w-3.5 h-3.5" />}
                      </div>
                      <div className="bg-white p-4 rounded-lg border shadow-sm">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-bold text-sm text-slate-900">{act.type === 'system' ? 'System Event' : profiles[act.created_by || ''] || 'Unknown Rep'}</span>
                          <span className="text-xs text-slate-500">{formatDistanceToNow(new Date(act.created_at), { addSuffix: true })}</span>
                        </div>
                        <div className="text-sm text-slate-600">{act.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ConfirmDialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen} onConfirm={handleDelete} title="Delete Customer" description="Are you sure you want to delete this customer? They will be removed from the active view, but their profile, deals, and communication history are preserved for auditing purposes." confirmLabel="Delete Customer" variant="destructive" />
    </div>
  );
}
